import { Fragment, useCallback, useMemo } from "react";
import {
  Box,
  Stack,
  Group,
  Text,
  Badge,
  Paper,
  Table,
  ActionIcon,
  Tooltip,
  Code,
} from "@mantine/core";
import {
  IconCheck,
  IconX,
  IconChevronDown,
  IconChevronRight,
  IconEdit,
} from "@tabler/icons-react";
import { Diagnosis } from "../../../services/admin/diagnosis.service";
import {
  parseVariables,
  replaceVariables,
} from "../../../services/internalGrossCode.service";

// Helper to strip HTML tags for plain text preview in table
const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

// Sample data for expanded row previews
const SAMPLE_DATA: Record<string, string> = {
  specimen_letter: "A",
  source: "Colon",
  subsite: "Ascending",
  location: "Colon, Ascending",
  collection_method: "Polypectomy",
  size_cm: "2.5",
  block_count: "2",
  color: "tan-pink",
  patient_name: "John Smith",
  case_number: "G26-00042",
  date_collected: "01/15/2026",
};

interface DiagnosisMacroRowProps {
  item: Diagnosis;
  canEdit: boolean;
  isExpanded: boolean;
  onToggleExpand: (id: string) => void;
  onEdit: (item: Diagnosis) => void;
  onToggleActive: (item: Diagnosis) => void;
}

export default function DiagnosisMacroRow({
  item,
  canEdit,
  isExpanded,
  onToggleExpand,
  onEdit,
  onToggleActive,
}: DiagnosisMacroRowProps) {
  const handleToggleExpand = useCallback(() => {
    onToggleExpand(item.id);
  }, [onToggleExpand, item.id]);

  const handleEdit = useCallback(() => {
    onEdit(item);
  }, [onEdit, item]);

  const handleToggleActive = useCallback(() => {
    onToggleActive(item);
  }, [onToggleActive, item]);

  const plainDescription = useMemo(
    () => stripHtml(item.diagnosisDescription),
    [item.diagnosisDescription]
  );

  const renderedDiagnosisHtml = useMemo(
    () => replaceVariables(item.diagnosisDescription, SAMPLE_DATA),
    [item.diagnosisDescription]
  );

  const renderedMicroscopicHtml = useMemo(
    () =>
      item.microscopicDescription
        ? replaceVariables(item.microscopicDescription, SAMPLE_DATA)
        : null,
    [item.microscopicDescription]
  );

  const templateVariables = useMemo(
    () =>
      parseVariables(
        item.diagnosisDescription + (item.microscopicDescription || "")
      ),
    [item.diagnosisDescription, item.microscopicDescription]
  );

  const icdCodeEntries = useMemo(() => {
    const entries: Array<{ code: string; description: string | null }> = [];
    if (item.icdCode) {
      entries.push({
        code: item.icdCode.code,
        description: item.icdCode.description || null,
      });
    }
    (item.additionalIcdCodes || [])
      .slice()
      .sort((a, b) => a.position - b.position)
      .forEach((link) => {
        if (link.icdCode?.code) {
          entries.push({
            code: link.icdCode.code,
            description: link.icdCode.description || null,
          });
        }
      });
    return entries;
  }, [item.icdCode, item.additionalIcdCodes]);

  return (
    <>
      <Table.Tr style={{ opacity: item.isActive ? 1 : 0.5 }}>
        {/* Expand Toggle */}
        <Table.Td>
          <ActionIcon variant="subtle" size="sm" onClick={handleToggleExpand}>
            {isExpanded ? (
              <IconChevronDown size={16} />
            ) : (
              <IconChevronRight size={16} />
            )}
          </ActionIcon>
        </Table.Td>

        {/* Code */}
        <Table.Td>
          <Text size="sm" fw={600} style={{ fontFamily: "monospace" }}>
            {item.code}
          </Text>
        </Table.Td>

        {/* Category */}
        <Table.Td>
          {item.diagnosisCategory?.name ? (
            <Badge color="blue" variant="light" size="sm">
              {item.diagnosisCategory.name}
            </Badge>
          ) : (
            <Text size="sm" c="dimmed">
              —
            </Text>
          )}
        </Table.Td>

        {/* CPT Code */}
        <Table.Td>
          {item.cptCode ? (
            <Tooltip label={item.cptCode.description}>
              <Text size="sm">{item.cptCode.code}</Text>
            </Tooltip>
          ) : (
            <Text size="sm" c="dimmed">
              —
            </Text>
          )}
        </Table.Td>

        {/* ICD Code */}
        <Table.Td>
          {icdCodeEntries.length > 0 ? (
            <Text size="sm">
              {icdCodeEntries.map((entry, index) => (
                <Fragment key={entry.code}>
                  <Tooltip
                    label={entry.description}
                    disabled={!entry.description}
                  >
                    <Text size="sm" span>
                      {entry.code}
                    </Text>
                  </Tooltip>
                  {index < icdCodeEntries.length - 1 ? ", " : null}
                </Fragment>
              ))}
            </Text>
          ) : (
            <Text size="sm" c="dimmed">
              —
            </Text>
          )}
        </Table.Td>

        {/* Diagnosis Description (plain text) */}
        <Table.Td>
          <Text size="sm" lineClamp={2}>
            {plainDescription || "—"}
          </Text>
        </Table.Td>

        {/* Scope */}
        <Table.Td>
          <Badge
            size="xs"
            variant="dot"
            color={item.scope === "personal" ? "violet" : "gray"}
          >
            {item.scope === "personal"
              ? item.createdBy?.fullName || "Personal"
              : "Global"}
          </Badge>
        </Table.Td>

        {/* Status */}
        <Table.Td>
          <Badge color={item.isActive ? "green" : "red"} variant="light">
            {item.isActive ? "Active" : "Inactive"}
          </Badge>
        </Table.Td>

        {/* Actions */}
        {canEdit && (
          <Table.Td>
            <Group gap="xs">
              <Tooltip label="Edit">
                <ActionIcon variant="subtle" color="blue" onClick={handleEdit}>
                  <IconEdit size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label={item.isActive ? "Deactivate" : "Activate"}>
                <ActionIcon
                  variant="subtle"
                  color={item.isActive ? "red" : "green"}
                  onClick={handleToggleActive}
                >
                  {item.isActive ? (
                    <IconX size={18} />
                  ) : (
                    <IconCheck size={18} />
                  )}
                </ActionIcon>
              </Tooltip>
            </Group>
          </Table.Td>
        )}
      </Table.Tr>

      {/* Expanded Row - Full Details */}
      {isExpanded && (
        <Table.Tr>
          <Table.Td colSpan={canEdit ? 9 : 8} p={0}>
            <Box pl="xl" pr="md" pb="md" pt="sm" bg="gray.0">
              <Group gap="xl" align="flex-start" wrap="wrap">
                {/* Left Column: Descriptions */}
                <Stack gap="md" style={{ flex: 2 }}>
                  {/* Full Diagnosis Description */}
                  <Stack gap={4}>
                    <Text size="xs" c="dimmed" fw={500}>
                      Full Diagnosis Description
                    </Text>
                    <Paper withBorder p="sm" bg="white">
                      <Box
                        style={{ fontSize: "14px" }}
                        dangerouslySetInnerHTML={{
                          __html: renderedDiagnosisHtml,
                        }}
                      />
                    </Paper>
                  </Stack>

                  {/* Microscopic Description */}
                  {renderedMicroscopicHtml && (
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed" fw={500}>
                        Microscopic Description
                      </Text>
                      <Paper withBorder p="sm" bg="white">
                        <Box
                          style={{ fontSize: "14px" }}
                          dangerouslySetInnerHTML={{
                            __html: renderedMicroscopicHtml,
                          }}
                        />
                      </Paper>
                    </Stack>
                  )}

                  {/* Comments */}
                  {item.comments && (
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed" fw={500}>
                        Comments
                      </Text>
                      <Box
                        style={{ fontSize: "14px" }}
                        dangerouslySetInnerHTML={{
                          __html: item.comments,
                        }}
                      />
                    </Stack>
                  )}
                </Stack>

                {/* Right Column: Variables & Metadata */}
                <Stack gap="md" style={{ flex: 1 }}>
                  {/* Template Variables */}
                  {templateVariables.length > 0 && (
                    <Stack gap={4}>
                      <Text size="xs" c="dimmed" fw={500}>
                        Template Variables
                      </Text>
                      <Group gap="xs">
                        {templateVariables.map((variable) => (
                          <Code
                            key={variable}
                            fz="sm"
                          >{`{{${variable}}}`}</Code>
                        ))}
                      </Group>
                    </Stack>
                  )}

                  {/* Billing Info */}
                  <Stack gap={4}>
                    <Text size="xs" c="dimmed" fw={500}>
                      Billing
                    </Text>
                    <Group gap="xs">
                      {item.cptCode ? (
                        <Badge variant="light" color="teal" size="sm">
                          CPT: {item.cptCode.code}
                        </Badge>
                      ) : (
                        <Text size="xs" c="dimmed">
                          No CPT code
                        </Text>
                      )}
                      {icdCodeEntries.length > 0 ? (
                        icdCodeEntries.map((entry) => (
                          <Tooltip
                            key={entry.code}
                            label={entry.description}
                            disabled={!entry.description}
                          >
                            <Badge variant="light" color="orange" size="sm">
                              ICD: {entry.code}
                            </Badge>
                          </Tooltip>
                        ))
                      ) : (
                        <Text size="xs" c="dimmed">
                          No ICD code
                        </Text>
                      )}
                    </Group>
                  </Stack>

                  {/* Category */}
                  <Stack gap={4}>
                    <Text size="xs" c="dimmed" fw={500}>
                      Category
                    </Text>
                    {item.diagnosisCategory?.name ? (
                      <Badge variant="light" color="blue" size="sm">
                        {item.diagnosisCategory.name}
                      </Badge>
                    ) : (
                      <Text size="xs" c="dimmed">
                        Uncategorized
                      </Text>
                    )}
                  </Stack>

                  {/* Linked Stains */}
                  <Stack gap={4}>
                    <Text size="xs" c="dimmed" fw={500}>
                      Auto-Linked Stains
                    </Text>
                    {item.stainLinks && item.stainLinks.length > 0 ? (
                      <Group gap="xs" wrap="wrap">
                        {item.stainLinks.map((link) => (
                          <Tooltip
                            key={link.id}
                            label={link.stain.description || ""}
                          >
                            <Badge variant="light" color="teal" size="sm">
                              {link.stain.code}
                            </Badge>
                          </Tooltip>
                        ))}
                      </Group>
                    ) : (
                      <Text size="xs" c="dimmed">
                        No linked stains
                      </Text>
                    )}
                  </Stack>
                </Stack>
              </Group>
            </Box>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}
